import { Injectable, inject } from '@angular/core';
import { EMPTY, Observable, catchError, debounceTime, filter, of, switchMap, tap, timeout } from 'rxjs';
import { VIEW_PREFS_RESET, ViewPreferencesStore } from './view-preferences.store';
import { ViewPreferencesService } from './view-preferences.service';
import { ViewPreferenceKey, ViewPreferenceState } from './view-preferences.types';

const SYNC_KEYS: ViewPreferenceKey[] = ['leads', 'requests', 'clients'];
const DEBOUNCE_MS = 1500;
const HYDRATE_TIMEOUT_MS = 8000;

@Injectable({ providedIn: 'root' })
export class ViewPreferencesSyncService {
    private readonly _service = inject(ViewPreferencesService);
    private readonly _store = inject(ViewPreferencesStore);

    constructor() {
        for (const key of SYNC_KEYS) {
            this._store
                .forKey(key)
                .changes$.pipe(
                    debounceTime(DEBOUNCE_MS),
                    // Logout emits the sentinel: it restarts the debounce window and is
                    // filtered out, so no PUT fires with a cleared token.
                    filter((change): change is ViewPreferenceState => change !== VIEW_PREFS_RESET),
                    switchMap((state) => this._service.update$(key, state).pipe(catchError(() => EMPTY))),
                )
                .subscribe();
        }
    }

    /** One-time session hydration. Never fails: any error falls back to empty defaults. */
    hydrate$(): Observable<unknown> {
        return this._service.findAll$().pipe(
            timeout({ first: HYDRATE_TIMEOUT_MS }),
            tap((prefs) => this._store.hydrateAll(prefs ?? [])),
            catchError(() => of(null)),
        );
    }
}
